import React from 'react';

import { Icon, Text } from '@betterment/samba-react';
import { Box, Flex, FormikRadioInput, Label, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import VisuallyHidden from '@reach/visually-hidden';
import { Formik, useFormikContext } from 'formik';

import { ORDERED_IMAGE_TYPES } from './constants';
import GoalImageIconPair from './GoalImageIconPair';
import ImageUploader from './ImageUploader';

import FlashErrorMessage from '../FlashErrorMessage';

import { GoalImageProps } from './index';

interface GoalImageSelectorProps {
  customImageData?: string;
  isCrypto: boolean;
  customCoverImageAllowed: boolean;
}

const noop = () => {
  // n o o p
};

const SelectedOptionContainer = styled(Flex)`
  cursor: pointer;

  align-items: center;
  border-radius: 11px;
  min-height: 170px;
  width: 120px;
`;

const GridContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  grid-gap: ${theme.space[0]};
  max-width: 576px;
`;

const GoalImageSelector: React.FC<React.PropsWithChildren<GoalImageSelectorProps>> = ({
  customCoverImageAllowed,
  isCrypto,
}) => {
  const { values } = useFormikContext<{
    imageType: GoalImageProps['name'];
    customImageData: string;
  }>();
  const isSelected = (imageName: GoalImageProps['name']) => imageName === values.imageType;

  return (
    <>
      <VisuallyHidden>
        <Text id="goal-image-selector">Select {isCrypto ? 'an account' : 'a goal'} image</Text>
      </VisuallyHidden>
      <GridContainer p="8" bg="bmtWhite" mx="auto" className="input-wrapper">
        {customCoverImageAllowed && <ImageUploader />}
        {ORDERED_IMAGE_TYPES.map(imageName => (
          <Label
            id={`goal-image-${imageName}-label`}
            htmlFor={`goal-image-${imageName}`}
            key={imageName}
            m="0"
            display="inline-block"
          >
            <VisuallyHidden>
              <FormikRadioInput
                id={`goal-image-${imageName}`}
                aria-labelledby={`goal-image-${imageName}-label goal-image-selector`}
                name="imageType"
                value={imageName}
              />
              <Text>{imageName}</Text>
            </VisuallyHidden>
            <SelectedOptionContainer
              flexDirection="column"
              p="1"
              bg={isSelected(imageName) ? 'bmtBlue' : undefined}
              mx="auto"
            >
              <GoalImageIconPair name={imageName} />
              {isSelected(imageName) && (
                <Flex mt="8">
                  <Icon name="Checkmark" size="small" color="icon.onPrimary" />
                  <Text
                    as="p"
                    color="text.inverse"
                    css={{
                      marginBottom: 'space.50',
                    }}
                  >
                    Selected
                  </Text>
                </Flex>
              )}
            </SelectedOptionContainer>
          </Label>
        ))}
      </GridContainer>
    </>
  );
};

export const FormikGoalImageSelector: React.FC<
  React.PropsWithChildren<{
    defaultGoalImage: GoalImageProps['name'];
    customCoverImageAllowed: boolean;
    isCrypto: boolean;
    customImageData?: string;
  }>
> = ({ defaultGoalImage, customCoverImageAllowed, isCrypto, customImageData }) => {
  return (
    <Formik initialValues={{ imageType: defaultGoalImage, customImageData }} onSubmit={noop}>
      <>
        <FlashErrorMessage name="customImageData" />
        <GoalImageSelector customCoverImageAllowed={customCoverImageAllowed} isCrypto={isCrypto} />
      </>
    </Formik>
  );
};

export default GoalImageSelector;
